// Shared "similar questions" helper for the three question-authoring surfaces:
//   - components/ingestion/ParsedDraftEditForm (CSV-parsed drafts)
//   - components/QuestionAddForm (new manual question)
//   - components/QuestionEditForm (existing question)
//
// Duplicate-detection only (Postgres pg_trgm via POST /ingestion/similar-questions).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiCaller } from './ApiUtilities';
import { getIngestionSimilarByTextUrl } from './UrlUtilities';
import { constructIngestionSimilarByTextPayload } from './PayloadUtils';
import { INGESTION_DEFAULT_TOP_K } from './Constants';

const useQuestionAi = ({
    questionFields,            // { question_text, option_a..d, correct_option, level }
    excludeQuestionIds = null, // pass [question_id] from Edit to hide self from similar results
}) => {
    const CallApi = useApiCaller();
    const callApiRef = useRef(CallApi);
    useEffect(() => { callApiRef.current = CallApi; });

    // Similar-questions state. `fetchSimilar` is exposed; the parent decides
    // when to auto-fetch (typically once on mount / on level change).
    const [similar, setSimilar] = useState([]);
    const [similarLoading, setSimilarLoading] = useState(false);
    const [similarFetched, setSimilarFetched] = useState(false);

    const fetchSimilar = useCallback(async () => {
        if (!questionFields.level) {
            // Empty-state copy in the panel nudges the user to pick a level.
            setSimilar([]);
            setSimilarFetched(false);
            return;
        }
        setSimilarLoading(true);
        setSimilarFetched(true);
        const res = await callApiRef.current({
            url: getIngestionSimilarByTextUrl(),
            method: 'POST',
            payload: constructIngestionSimilarByTextPayload(
                questionFields,
                INGESTION_DEFAULT_TOP_K,
                excludeQuestionIds,
            ),
            token: true,
        });
        if (res.success) setSimilar(res.data.similar || []);
        setSimilarLoading(false);
    }, [questionFields, excludeQuestionIds]);

    return {
        similar,
        similarLoading,
        similarFetched,
        fetchSimilar,
    };
};

export default useQuestionAi;
